/**
 * Dropdown Menu Component
 * Handles hover interactions for the dropdown menu system
 *
 * Safari fixes:
 *  - Uses pointerenter/pointerleave (more reliable in Safari than mouse events)
 *  - Verifies cursor position before closing to prevent false mouseleave
 *  - Longer close delay to survive Safari's event timing quirks
 */
class DropdownMenu extends HTMLElement {
  constructor() {
    super();
    this.items = [];
    this.activeItem = null;
    this.closeTimeout = null;
  }

  connectedCallback() {
    this.items = this.querySelectorAll('.dropdown-menu__item--has-children');

    this.items.forEach(item => {
      const link = item.querySelector('.dropdown-menu__link');
      const submenu = item.querySelector('.dropdown-menu__submenu');

      if (!link || !submenu) return;

      // Use pointerenter/pointerleave — they are more reliable in Safari
      // than mouseenter/mouseleave on elements that toggle visibility.
      item.addEventListener('pointerenter', () => {
        this.clearCloseTimeout();
        this.openSubmenu(item, link);
      });

      item.addEventListener('pointerleave', (e) => {
        // Safari sometimes fires a spurious pointerleave when the submenu
        // first becomes visible. Double-check that the pointer actually left
        // the item bounding box before scheduling a close.
        const rect = item.getBoundingClientRect();
        const submenuRect = submenu.getBoundingClientRect();
        const x = e.clientX;
        const y = e.clientY;

        // Combine item + submenu rects for the "safe zone"
        const inItem = x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
        const inSubmenu = submenuRect.width > 0 &&
          x >= submenuRect.left && x <= submenuRect.right &&
          y >= submenuRect.top && y <= submenuRect.bottom;

        if (inItem || inSubmenu) {
          // Pointer is still inside — ignore the leave event
          return;
        }

        this.closeTimeout = setTimeout(() => {
          this.closeSubmenu(item, link);
        }, 250);
      });

      // Keyboard navigation
      link.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          const isExpanded = link.getAttribute('aria-expanded') === 'true';
          if (isExpanded) {
            this.closeSubmenu(item, link);
          } else {
            this.openSubmenu(item, link);
          }
        } else if (e.key === 'Escape') {
          this.closeSubmenu(item, link);
          link.focus();
        }
      });

      // Close on focus out
      item.addEventListener('focusout', (e) => {
        if (!item.contains(e.relatedTarget)) {
          this.closeSubmenu(item, link);
        }
      });
    });

    // Close all submenus when clicking outside
    document.addEventListener('click', (e) => {
      if (!this.contains(e.target)) {
        this.closeAllSubmenus();
      }
    });
  }

  clearCloseTimeout() {
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout);
      this.closeTimeout = null;
    }
  }

  openSubmenu(item, link) {
    // Close other open submenus first
    this.items.forEach(otherItem => {
      if (otherItem !== item) {
        const otherLink = otherItem.querySelector('.dropdown-menu__link');
        this.closeSubmenu(otherItem, otherLink);
      }
    });

    item.classList.add('dropdown-menu__item--open');
    link.setAttribute('aria-expanded', 'true');
    this.activeItem = item;

    // Clamp submenu within viewport so it doesn't overflow left or right
    const submenu = item.querySelector('.dropdown-menu__submenu');
    if (submenu) {
      // Reset any previous adjustment
      submenu.style.left = '';
      submenu.style.right = '';
      submenu.style.transform = '';

      // Wait one frame for the browser to lay out the now-visible submenu
      requestAnimationFrame(() => {
        const rect = submenu.getBoundingClientRect();
        const pad = 8; // px of breathing room from viewport edge

        if (rect.left < pad) {
          // Overflowing left — shift right
          const shift = pad - rect.left;
          submenu.style.left = `calc(50% + ${shift}px)`;
        } else if (rect.right > window.innerWidth - pad) {
          // Overflowing right — shift left
          const shift = rect.right - (window.innerWidth - pad);
          submenu.style.left = `calc(50% - ${shift}px)`;
        }
      });
    }
  }

  closeSubmenu(item, link) {
    item.classList.remove('dropdown-menu__item--open');
    link.setAttribute('aria-expanded', 'false');

    // Reset any viewport-clamp adjustments
    const submenu = item.querySelector('.dropdown-menu__submenu');
    if (submenu) {
      submenu.style.left = '';
      submenu.style.right = '';
      submenu.style.transform = '';
    }

    if (this.activeItem === item) {
      this.activeItem = null;
    }
  }

  closeAllSubmenus() {
    this.items.forEach(item => {
      const link = item.querySelector('.dropdown-menu__link');
      this.closeSubmenu(item, link);
    });
  }
}

if (!customElements.get('dropdown-menu')) {
  customElements.define('dropdown-menu', DropdownMenu);
}
